import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, X, Clock, User } from 'lucide-react';
import { format } from 'date-fns';

interface CommunityRequest {
  id: string;
  user_id: string;
  community_name: string;
  description: string | null;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  profiles?: {
    username: string | null;
    farm_name: string | null;
  } | null;
}

export const CommunityRequestsAdmin = () => {
  const [requests, setRequests] = useState<CommunityRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('community_requests')
        .select(`
          id,
          user_id,
          community_name,
          description,
          status,
          created_at,
          reviewed_by,
          reviewed_at
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching community requests:', error);
        return;
      }

      setRequests(data || []);
    } catch (err) {
      console.error('Unexpected error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const updateRequestStatus = async (requestId: string, status: 'approved' | 'rejected') => {
    try {
      // First get the request details
      const { data: request, error: requestError } = await supabase
        .from('community_requests')
        .select('*')
        .eq('id', requestId)
        .single();

      if (requestError || !request) {
        toast({
          title: "Error",
          description: "Failed to get request details",
          variant: "destructive",
        });
        return;
      }

      // If approving, create the community and add user as member
      if (status === 'approved') {
        // Check if community already exists
        const { data: existingCommunity } = await supabase
          .from('communities')
          .select('id')
          .eq('name', request.community_name)
          .single();

        let communityId = existingCommunity?.id;

        // Create community if it doesn't exist
        if (!existingCommunity) {
          const { data: newCommunity, error: communityError } = await supabase
            .from('communities')
            .insert({
              name: request.community_name,
              description: request.description,
              members_count: 0, // Will be updated by trigger
            })
            .select('id')
            .single();

          if (communityError || !newCommunity) {
            toast({
              title: "Error",
              description: "Failed to create community",
              variant: "destructive",
            });
            return;
          }
          communityId = newCommunity.id;
        }

        // Get the user's profile ID
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('id')
          .eq('user_id', request.user_id)
          .single();

        if (profileError || !profile) {
          toast({
            title: "Error",
            description: "Failed to find user profile",
            variant: "destructive",
          });
          return;
        }

        // Add user to the community
        const { error: membershipError } = await supabase
          .from('user_communities')
          .insert({
            profile_id: profile.id,
            community_id: communityId,
          });

        if (membershipError && membershipError.code !== '23505') { // Ignore if already exists
          toast({
            title: "Error",
            description: "Failed to add user to community",
            variant: "destructive",
          });
          return;
        }
      }

      // Update request status
      const { error } = await supabase
        .from('community_requests')
        .update({
          status,
          reviewed_by: (await supabase.auth.getUser()).data.user?.id,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', requestId);

      if (error) {
        toast({
          title: "Error",
          description: "Failed to update request status",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success",
        description: status === 'approved' 
          ? "Request approved and community created/joined successfully" 
          : "Request rejected successfully",
      });

      fetchRequests();
    } catch (err) {
      console.error('Error updating status:', err);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-4">
              <div className="h-4 bg-muted rounded w-1/4 mb-2" />
              <div className="h-3 bg-muted rounded w-3/4 mb-2" />
              <div className="h-3 bg-muted rounded w-1/2" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return <Check className="w-3 h-3" />;
      case 'rejected': return <X className="w-3 h-3" />;
      default: return <Clock className="w-3 h-3" />;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Community Requests</h3>
        <Badge variant="outline">
          {requests.filter(r => r.status === 'pending').length} pending
        </Badge>
      </div>

      {requests.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-muted-foreground">No community requests found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {requests.map((request) => (
            <Card key={request.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium">{request.community_name}</h4>
                      <Badge className={`text-xs ${getStatusColor(request.status)}`}>
                        {getStatusIcon(request.status)}
                        <span className="ml-1 capitalize">{request.status}</span>
                      </Badge>
                    </div>
                    
                    {request.description && (
                      <p className="text-sm text-muted-foreground">{request.description}</p>
                    )}
                    
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        User ID: {request.user_id.slice(0, 8)}...
                      </div>
                      <div>
                        Requested: {format(new Date(request.created_at), 'MMM dd, yyyy')}
                      </div>
                      {request.reviewed_at && (
                        <div>
                          Reviewed: {format(new Date(request.reviewed_at), 'MMM dd, yyyy')}
                        </div>
                      )}
                    </div>
                  </div>

                  {request.status === 'pending' && (
                    <div className="flex gap-2 ml-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateRequestStatus(request.id, 'approved')}
                        className="text-green-600 border-green-600 hover:bg-green-50"
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateRequestStatus(request.id, 'rejected')}
                        className="text-red-600 border-red-600 hover:bg-red-50"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};